import { useAuthStore } from '@/store/auth.store';
import { isImpersonating } from '@/utils/impersonation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { assistantService, type EtatAnnonce } from '../services/assistant.service';

/** Au-delà, on laisse passer pour la session : la preuve n'étant pas écrite, l'écran reviendra. */
export const ECHECS_AVANT_CONTOURNEMENT = 2;
export const MESSAGE_CONTOURNEMENT =
  'La validation sera redemandée à la prochaine connexion.';

/**
 * L'annonce de l'assistant à la première connexion (décision Rose 12/09/2026).
 *
 * Le serveur seul décide si l'écran doit être présenté : assistant activé,
 * profil qui y a accès, version du texte pas encore validée. N'interrogent
 * même pas le serveur : un accès coffre (salarié), une entreprise cliente
 * seule (`actif` = false tant que le profil n'est pas connu, puis selon lui —
 * l'assistant n'est pas dans sa navigation), et une session « se connecter
 * en tant que » (l'administrateur fait son support ; le client verra l'écran
 * à sa vraie connexion, et rien n'est jamais validé en son nom).
 *
 * Personne n'est enfermé (verdict sécurité) :
 * - le GET a cinq secondes, sans nouvel essai, et l'application se rend
 *   pendant qu'on attend — l'écran ne s'impose qu'à la réponse « à valider » ;
 * - si l'enregistrement de la validation échoue deux fois de suite, on laisse
 *   passer pour la session : la preuve n'est pas écrite, l'écran reviendra à
 *   la prochaine connexion.
 *
 * La clé porte l'utilisateur : le cache n'est pas vidé à la déconnexion, et
 * un second compte ouvert dans le même onglet ne doit pas hériter de la
 * validation du premier.
 */
export function useAnnonceAssistant({ actif }: { actif: boolean }) {
  const { user, isAuthenticated } = useAuthStore();
  const client = useQueryClient();
  const eligible =
    actif &&
    isAuthenticated &&
    !!user &&
    user.role !== 'vault_user' &&
    !isImpersonating();
  const cle = ['assistant', 'annonce', user?.uuid] as const;
  const requete = useQuery({
    queryKey: cle,
    queryFn: () => assistantService.annonce(),
    enabled: eligible,
    staleTime: Infinity,
    retry: 0,
  });
  /** Échecs consécutifs de l'enregistrement — un ref, jamais une valeur périmée. */
  const echecs = useRef(0);
  const [contournee, setContournee] = useState(false);
  const validation = useMutation({
    mutationFn: () => assistantService.validerAnnonce(),
    onSuccess: (etat: EtatAnnonce) => {
      echecs.current = 0;
      client.setQueryData(cle, etat);
    },
    onError: () => {
      echecs.current += 1;
      if (echecs.current >= ECHECS_AVANT_CONTOURNEMENT) {
        setContournee(true);
        toast.message(MESSAGE_CONTOURNEMENT);
      }
    },
  });
  return {
    /** true seulement quand le serveur l'a dit ; jamais supposé. */
    aValider: eligible && !contournee && requete.data?.aValider === true,
    version: requete.data?.version,
    valider: validation.mutateAsync,
    validationEnCours: validation.isPending,
    validationEnErreur: validation.isError,
  };
}
